import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Copy, Loader2, X, AlertTriangle, KeyRound, MessageCircle } from "lucide-react";
import { toast } from "sonner";

import { confirmar } from "@/components/ConfirmDialog";
const ROLES = [
  { v: "aluno", l: "Aluno(a)" },
  { v: "professor", l: "Professor(a)" },
  { v: "instrutor", l: "Instrutor(a)" },
  { v: "mentor", l: "Mentor(a)" },
  { v: "representante", l: "Representante Credenciado" },
  { v: "admin", l: "Administrador(a)" },
];

const UFS = [
  ["AC", "Acre"], ["AL", "Alagoas"], ["AP", "Amapá"], ["AM", "Amazonas"], ["BA", "Bahia"], ["CE", "Ceará"],
  ["DF", "Distrito Federal"], ["ES", "Espírito Santo"], ["GO", "Goiás"], ["MA", "Maranhão"], ["MT", "Mato Grosso"],
  ["MS", "Mato Grosso do Sul"], ["MG", "Minas Gerais"], ["PA", "Pará"], ["PB", "Paraíba"], ["PR", "Paraná"],
  ["PE", "Pernambuco"], ["PI", "Piauí"], ["RJ", "Rio de Janeiro"], ["RN", "Rio Grande do Norte"],
  ["RS", "Rio Grande do Sul"], ["RO", "Rondônia"], ["RR", "Roraima"], ["SC", "Santa Catarina"], ["SP", "São Paulo"],
  ["SE", "Sergipe"], ["TO", "Tocantins"],
];
const plain = (t) => (t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();

/** A turma "combina" com o estado se o nome dele traz a UF (ex.: "Keyla - PE") ou o nome do estado. */
function matchesState(groupName, uf) {
  if (!uf) return false;
  const name = plain(groupName);
  const full = plain(UFS.find(([u]) => u === uf)?.[1]);
  return new RegExp(`(^|[^A-Z])${uf}([^A-Z]|$)`).test(name) || (full && name.includes(full));
}

const EMPTY = { name: "", email: "", phone: "", role: "", state: "", groups: false, courses: false, group_ids: [], course_ids: [], send_invite: true, bonus: false, bonus_fee: "" };

const monthsLabel = (n) => (!n ? "sem prazo" : n % 12 === 0 ? (n === 12 ? "1 ano" : `${n / 12} anos`) : n === 1 ? "1 mês" : `${n} meses`);
function addMonths(n) {
  const d = new Date(); const day = d.getDate();
  d.setDate(1); d.setMonth(d.getMonth() + n);
  d.setDate(Math.min(day, new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()));
  return d.toLocaleDateString("pt-BR");
}

/** Sim / Não em botões lado a lado, como na plataforma antiga. */
const YesNo = ({ value, onChange, testid }) => (
  <div className="inline-grid grid-cols-2 w-full sm:w-80 rounded-md overflow-hidden border border-slate-200" data-testid={testid}>
    {[true, false].map((v) => (
      <button
        key={String(v)}
        type="button"
        onClick={() => onChange(v)}
        className={`h-10 text-sm font-medium transition-colors ${
          value === v ? "bg-[#232D4F] text-white" : "bg-white text-slate-600 hover:bg-slate-50"
        }`}
      >
        {v ? "Sim" : "Não"}
      </button>
    ))}
  </div>
);

// curso presencial: cidade/UF e data, para diferenciar cursos com o mesmo nome
const dataBR = (d) => (d ? d.slice(8, 10) + "/" + d.slice(5, 7) : "");
function presencialInfo(c) {
  if (c.modality !== "presencial") return "";
  const local = [c.city, c.state].filter(Boolean).join("/");
  const ini = dataBR(c.event_date), fim = dataBR(c.event_end);
  const quando = ini ? (fim && fim !== ini ? `${ini} a ${fim}` : ini) + (c.event_date ? "/" + c.event_date.slice(0, 4) : "") : "";
  return ["Presencial", local, quando].filter(Boolean).join(" · ");
}

const Req = () => <span className="text-red-500 mr-1">*</span>;

const PickList = ({ items, selected, onToggle, empty }) => (
  <div className="mt-3 max-h-44 overflow-y-auto iep-scroll rounded-md border border-slate-200 divide-y divide-slate-100">
    {items.length === 0 && <p className="p-3 text-sm text-slate-400">{empty}</p>}
    {items.map((it) => (
      <label key={it.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
        <Checkbox checked={selected.includes(it.id)} onCheckedChange={() => onToggle(it.id)} />
        <span className="text-slate-700 flex-1">
          {it.label}
          {it.suggested && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-white bg-[#1E9E2A] rounded px-1.5 py-0.5">sugerido</span>}
          {it.sub && <span className="block text-[11px] leading-tight font-medium text-[#1E9E2A]">{it.sub}</span>}
        </span>
        {it.hint && <span className={`text-xs ${it.hint.startsWith("Sem") ? "text-slate-400" : "text-[#1E9E2A] font-medium"}`}>{it.hint}</span>}
      </label>
    ))}
  </div>
);

export const NewUserDialog = ({ open, onOpenChange, onCreated, studentOnly = false, defaultRole = "" }) => {
  const [f, setF] = useState(EMPTY);
  const [groups, setGroups] = useState([]);
  const [courses, setCourses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setF(studentOnly ? { ...EMPTY, role: "aluno" } : { ...EMPTY, role: defaultRole });
    setResult(null);
    api.get("/groups").then((r) => setGroups(r.data.map((g) => ({
      id: g.id,
      label: g.name,
      hint: g.instructor_name ? `Professor(a): ${g.instructor_name}` : "Sem professor definido",
    })))).catch(() => setGroups([]));
    api.get("/courses").then((r) => setCourses(r.data.map((c) => ({ id: c.id, label: c.title + (c.published ? "" : " (rascunho)"), months: c.access_months || 0, title: c.title, sub: presencialInfo(c) })))).catch(() => setCourses([]));
  }, [open]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // verifica, enquanto digita, se o e-mail já existe e se há alguém com o mesmo nome
  const [check, setCheck] = useState({ email_in_use: "", same_name: [] });
  const [checking, setChecking] = useState(false);
  useEffect(() => {
    if (!open || result) return;
    const email = /\S+@\S+\.\S+/.test(f.email) ? f.email.trim() : "";
    const name = f.name.trim().length >= 5 ? f.name.trim() : "";
    if (!email && !name) { setCheck({ email_in_use: "", same_name: [] }); return; }
    setChecking(true);
    const t = setTimeout(() => {
      api.get("/admin/users/check", { params: { email, name } })
        .then((r) => setCheck(r.data)).catch(() => {}).finally(() => setChecking(false));
    }, 400);
    return () => clearTimeout(t);
  }, [f.email, f.name, open, result]);
  const toggle = (k, id) => setF((p) => ({ ...p, [k]: p[k].includes(id) ? p[k].filter((x) => x !== id) : [...p[k], id] }));
  const isStudent = f.role === "aluno";
  const sortedGroups = groups
    .map((g) => ({ ...g, suggested: matchesState(g.label, f.state) }))
    .sort((a, b) => (b.suggested - a.suggested) || a.label.localeCompare(b.label));

  const submit = async () => {
    if (!f.name.trim()) return toast.error("Insira o nome completo.");
    if (!f.role) return toast.error("Selecione uma permissão.");
    if (!f.email.trim()) return toast.error("Insira um e-mail.");
    if (check.email_in_use) return toast.error("Este e-mail já está cadastrado. Não é possível criar outro usuário com ele.");
    if (check.same_name.length && !(await confirmar(`Já existe cadastro com o nome "${f.name.trim()}". Criar mesmo assim (pessoa diferente)?`))) return;
    if (isStudent && f.groups && f.group_ids.length === 0) return toast.error("Selecione ao menos uma turma (ou marque Não).");
    if (isStudent && f.courses && f.course_ids.length === 0) return toast.error("Selecione ao menos um curso (ou marque Não).");
    setSaving(true);
    try {
      const r = await api.post("/admin/users", {
        name: f.name, email: f.email, phone: f.phone, role: f.role, send_invite: f.send_invite, state: isStudent ? f.state : "",
        group_ids: isStudent && f.groups ? f.group_ids : [],
        course_ids: isStudent && f.courses ? f.course_ids : [],
        bonus: isStudent && f.courses && f.bonus,
        bonus_fee: isStudent && f.courses && f.bonus && String(f.bonus_fee).trim() !== "" ? parseFloat(String(f.bonus_fee).replace(/\./g, "").replace(",", ".")) : null,
      });
      setResult(r.data);
      onCreated?.();
      toast.success("Usuário criado!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const copy = (t, msg = "Copiado!") => { navigator.clipboard.writeText(t); toast.success(msg); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden [&>button]:hidden" data-testid="new-user-dialog">
        <div className="flex items-center justify-between px-6 py-4 text-white" style={{ background: "#232D4F" }}>
          <DialogTitle className="font-display text-xl font-semibold text-white">{f.role === "aluno" ? "Novo aluno" : "Novo usuário"}</DialogTitle>
          <button onClick={() => onOpenChange(false)} className="text-white/80 hover:text-white" aria-label="Fechar"><X className="h-5 w-5" /></button>
        </div>
        <DialogDescription className="sr-only">Cadastro manual de aluno, professor ou administrador.</DialogDescription>

        {result ? (
          <div className="p-6 space-y-4">
            <div className="rounded-lg bg-green-50 border border-green-100 p-5 text-center">
              <CheckCircle2 className="h-9 w-9 text-[#1E9E2A] mx-auto mb-2" />
              <p className="font-semibold text-slate-800">{result.name} cadastrado(a)!</p>
              <p className="text-sm text-slate-500 mt-1">
                {result.email_sent
                  ? `E-mail de 1º acesso enviado${result.whatsapp_sent ? " e WhatsApp enviado" : ""}.`
                  : f.send_invite
                    ? "Não foi possível enviar o e-mail — envie os dados abaixo por WhatsApp."
                    : "Nenhum e-mail enviado — envie os dados abaixo para a pessoa."}
              </p>
              {(result.groups > 0 || result.courses > 0) && (
                <p className="text-xs text-slate-500 mt-2">Incluído em {result.groups} turma(s) e matriculado em {result.courses} curso(s).</p>
              )}
            </div>
            {result.code && (
              <div className="rounded-lg border border-slate-200 p-4 space-y-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <KeyRound className="h-5 w-5 text-[#1E9E2A]" />
                  <span className="text-sm text-slate-600">Código de 1º acesso:</span>
                  <span className="font-mono text-xl font-bold tracking-widest text-[#232D4F]" data-testid="new-user-code">{result.code}</span>
                </div>
                <textarea readOnly value={result.message} rows={5} className="w-full rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700" />
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => copy(result.message, "Mensagem copiada!")}><Copy className="h-4 w-4 mr-1.5" /> Copiar mensagem</Button>
                  <Button variant="outline" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(result.message)}`, "_blank")}><MessageCircle className="h-4 w-4 mr-1.5" /> Enviar por WhatsApp</Button>
                </div>
                <p className="text-xs text-slate-400">No 1º acesso a pessoa completa o cadastro e cria a própria senha; o código deixa de valer.</p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
              <Button onClick={() => { setF(studentOnly ? { ...EMPTY, role: "aluno" } : { ...EMPTY, role: defaultRole }); setResult(null); }} className="text-white" style={{ background: "#1E9E2A" }}>Cadastrar outro</Button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-6 space-y-7 max-h-[70vh] overflow-y-auto iep-scroll">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="font-semibold text-slate-800"><Req />Nome completo</label>
                  <Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Insira o nome completo" className="h-11" data-testid="new-user-name" />
                  {check.same_name.length > 0 && (
                    <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2" data-testid="new-user-name-warning">
                      <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />Já existe cadastro com este nome:
                      {check.same_name.map((p) => <div key={p.id} className="ml-5">• {p.name} ({p.kind}) — {p.email}</div>)}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="font-semibold text-slate-800"><Req />Permissão</label>
                  {studentOnly ? <Input value="Aluno(a)" readOnly className="h-11" /> : (
                    <Select value={f.role} onValueChange={(v) => set("role", v)}>
                      <SelectTrigger className="h-11" data-testid="new-user-role"><SelectValue placeholder="Selecione uma permissão" /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="font-semibold text-slate-800"><Req />E-mail</label>
                <Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="Insira um e-mail"
                  className={`h-11 ${check.email_in_use ? "border-red-400 focus-visible:ring-red-400" : ""}`} data-testid="new-user-email" />
                {checking && <p className="text-xs text-slate-400">Verificando…</p>}
                {check.email_in_use && (
                  <p className="text-xs text-red-600 font-medium" data-testid="new-user-email-in-use">
                    Este e-mail já está cadastrado ({check.email_in_use}). Não é possível criar outro usuário com ele.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label className="font-semibold text-slate-800">Celular (WhatsApp)</label>
                <Input type="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(00) 00000-0000" className="h-11" data-testid="new-user-phone" />
                <p className="text-xs text-slate-400">Com o convite ativado, o aluno recebe também pelo WhatsApp o link para atualizar os dados.</p>
              </div>

              {isStudent && (
                <>
                  <div className="space-y-2 sm:w-80">
                    <label className="font-semibold text-slate-800">Estado (UF) do aluno</label>
                    <Select value={f.state} onValueChange={(v) => set("state", v)}>
                      <SelectTrigger className="h-11" data-testid="new-user-state"><SelectValue placeholder="Selecione o estado" /></SelectTrigger>
                      <SelectContent>{UFS.map(([u, n]) => <SelectItem key={u} value={u}>{u} — {n}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 mb-1">Incluir em turmas?</p>
                    <p className="text-xs text-slate-400 mb-3">A turma define com qual professor(a) o aluno fica.</p>
                    <YesNo value={f.groups} onChange={(v) => set("groups", v)} testid="new-user-groups" />
                    {f.groups && <PickList items={sortedGroups} selected={f.group_ids} onToggle={(id) => toggle("group_ids", id)} empty="Nenhuma turma criada. Crie em Turmas e defina o professor(a)." />}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 mb-3">Matricular em cursos?</p>
                    <YesNo value={f.courses} onChange={(v) => set("courses", v)} testid="new-user-courses" />
                    {f.courses && <PickList items={courses} selected={f.course_ids} onToggle={(id) => toggle("course_ids", id)} empty="Nenhum curso cadastrado." />}
                    {f.courses && f.course_ids.length > 0 && (
                      <div className={`mt-3 rounded-lg border p-3 ${f.bonus ? "border-green-300 bg-green-50" : "border-slate-200"}`} data-testid="new-user-bonus-box">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <Checkbox checked={f.bonus} onCheckedChange={(v) => set("bonus", !!v)} data-testid="new-user-bonus" />
                          <span className="font-semibold text-slate-800">Bônus de matrícula curso presencial</span>
                        </label>
                        <p className="text-xs text-slate-500 mt-1 ml-7">O acesso segue o prazo definido em cada curso, contado a partir de hoje.</p>
                        {f.bonus && (
                          <div className="mt-3 ml-7" data-testid="new-user-bonus-fee-box">
                            <label className="text-sm font-medium text-slate-800">Taxa que a professora paga à escola (R$) <span className="font-normal text-slate-500">— por curso bonificado</span></label>
                            <input value={f.bonus_fee} onChange={(e) => set("bonus_fee", e.target.value)} inputMode="decimal" placeholder="Ex.: 150,00 (deixe em branco se não houver)"
                              className="mt-1 h-10 w-full max-w-xs rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="new-user-bonus-fee" />
                            <p className="text-xs text-slate-500 mt-1">Fica registrado em Financeiro → Taxas de bônus, em nome da professora da turma escolhida.</p>
                          </div>
                        )}
                        {f.bonus && (
                          <ul className="mt-2 ml-7 space-y-1 text-sm">
                            {courses.filter((c) => f.course_ids.includes(c.id)).map((c) => (
                              <li key={c.id} className="text-slate-700">
                                • {c.title}: {c.months
                                  ? <strong className="text-[#1E9E2A]">acesso até {addMonths(c.months)} ({monthsLabel(c.months)})</strong>
                                  : <span className="text-amber-700">curso sem prazo definido — acesso sem prazo</span>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              <div>
                <p className="font-semibold text-slate-800 mb-3">Enviar e-mail de 1º acesso?</p>
                <YesNo value={f.send_invite} onChange={(v) => set("send_invite", v)} testid="new-user-invite" />
                <p className="text-xs text-slate-400 mt-2">
                  {f.send_invite
                    ? "A pessoa recebe o link da tela de login, o e-mail de acesso e um código gerado automaticamente."
                    : "Sem e-mail: o código aparece na tela para você enviar (ex.: WhatsApp)."}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full px-6 uppercase tracking-wide">Cancelar</Button>
              <Button onClick={submit} disabled={saving || !!check.email_in_use} className="rounded-full px-8 uppercase tracking-wide text-white" style={{ background: "#1E9E2A" }} data-testid="new-user-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Criar"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
